/**
 * SharePoint Assistant — collapsible right-side chat panel for the SharePoint page
 * (Estimates + Projects). Covers the right side from under the header to the bottom.
 *
 * Talks to POST /api/sharepoint-assistant/chat (SSE). The backend agent can search
 * ONLY inside this project/estimate's SharePoint root, create personal bookmarks,
 * and send bookmark share invites to assigned teammates.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { getAuthToken } from '../../utils/apiClient';

const API_BASE = process.env.REACT_APP_API_URL
  ? (process.env.REACT_APP_API_URL.endsWith('/api')
      ? process.env.REACT_APP_API_URL
      : `${process.env.REACT_APP_API_URL.replace(/\/?$/, '')}/api`)
  : '/api';

const OPEN_KEY = 'sp-assistant-open';

/** Minimal markdown: bold, italics, code, lists, [text](url) links. */
function renderMarkdown(text) {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#0f766e;font-weight:600;word-break:break-word">$1</a>'
  );
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  html = html.replace(
    /`([^`]+)`/g,
    '<code style="background:#f0f0f0;padding:1px 4px;border-radius:3px;font-size:0.82em">$1</code>'
  );
  const lines = html.split('\n');
  const result = [];
  let inList = false;
  let listType = null;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const numberedMatch = line.match(/^(\d+)\.\s+(.*)/);
    const bulletMatch = line.match(/^\s*[-•]\s+(.*)/);
    if (numberedMatch) {
      if (!inList || listType !== 'ol') {
        if (inList) result.push(listType === 'ol' ? '</ol>' : '</ul>');
        result.push('<ol style="margin:4px 0;padding-left:22px">');
        inList = true; listType = 'ol';
      }
      result.push(`<li style="margin-bottom:2px">${numberedMatch[2]}</li>`);
    } else if (bulletMatch) {
      if (!inList || listType !== 'ul') {
        if (inList) result.push(listType === 'ol' ? '</ol>' : '</ul>');
        result.push('<ul style="margin:4px 0;padding-left:22px">');
        inList = true; listType = 'ul';
      }
      result.push(`<li style="margin-bottom:2px">${bulletMatch[1]}</li>`);
    } else {
      if (inList) {
        result.push(listType === 'ol' ? '</ol>' : '</ul>');
        inList = false; listType = null;
      }
      result.push(line === '' ? '<br/>' : line + (i < lines.length - 1 ? '<br/>' : ''));
    }
  }
  if (inList) result.push(listType === 'ol' ? '</ol>' : '</ul>');
  return result.join('');
}

const TOOL_ICONS = {
  search_files: '🔍',
  list_folder: '📂',
  create_bookmark: '⭐',
  list_bookmarks: '⭐',
  list_team_members: '👥',
  share_bookmark: '📨',
};

const SharePointAssistant = ({ projectId = null, estimateId = null, scopeNoun = 'project', onBookmarksChanged }) => {
  const { instance, accounts } = useMsal();

  const welcome = {
    role: 'assistant',
    content:
      `Hi! I can help with **this ${scopeNoun}'s SharePoint folder only**:\n` +
      `- find documents (e.g. "find the GA drawing")\n` +
      `- bookmark them for you\n` +
      `- share your bookmarks with teammates (they accept an invite)\n\n` +
      `What are you looking for?`,
  };

  const [isOpen, setIsOpen] = useState(() => {
    try {
      const saved = window.localStorage.getItem(OPEN_KEY);
      if (saved === '1') return true;
      if (saved === '0') return false;
    } catch { /* ignore */ }
    return typeof window !== 'undefined' && window.innerWidth >= 1024;
  });
  const [messages, setMessages] = useState([welcome]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const streamingIdxRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const toggleOpen = (next) => {
    setIsOpen(next);
    try { window.localStorage.setItem(OPEN_KEY, next ? '1' : '0'); } catch { /* ignore */ }
  };

  const clearChat = () => {
    abortRef.current?.abort();
    streamingIdxRef.current = null;
    setIsLoading(false);
    setMessages([welcome]);
  };

  const applyEvent = useCallback((evt) => {
    if (evt.content) {
      setMessages((prev) => {
        const updated = [...prev];
        if (
          streamingIdxRef.current != null &&
          updated[streamingIdxRef.current] &&
          updated[streamingIdxRef.current].role === 'assistant'
        ) {
          updated[streamingIdxRef.current] = {
            ...updated[streamingIdxRef.current],
            content: updated[streamingIdxRef.current].content + evt.content,
          };
        } else {
          updated.push({ role: 'assistant', content: evt.content });
          streamingIdxRef.current = updated.length - 1;
        }
        return updated;
      });
      return;
    }
    if (evt.tool) {
      const { name, status, label } = evt.tool;
      streamingIdxRef.current = null; // next content starts a fresh bubble
      setMessages((prev) => {
        const updated = [...prev];
        if (status === 'running') {
          updated.push({ role: 'tool', name, status, content: label });
        } else {
          // Replace the most recent running chip for the same tool
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].role === 'tool' && updated[i].name === name && updated[i].status === 'running') {
              updated[i] = { ...updated[i], status, content: label };
              return updated;
            }
          }
          updated.push({ role: 'tool', name, status, content: label });
        }
        return updated;
      });
      return;
    }
    if (evt.bookmarksChanged && typeof onBookmarksChanged === 'function') {
      onBookmarksChanged();
    }
    if (evt.error) {
      streamingIdxRef.current = null;
      setMessages((prev) => [...prev, { role: 'assistant', content: `⚠️ ${evt.error}` }]);
    }
  }, [onBookmarksChanged]);

  const sendMessage = async (textOverride = null) => {
    const text = String(textOverride ?? input).trim();
    if (!text || isLoading) return;

    const history = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));
    const outgoing = [...history, { role: 'user', content: text }];

    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setIsLoading(true);
    streamingIdxRef.current = null;

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = await getAuthToken(instance, accounts);
      const response = await fetch(`${API_BASE}/sharepoint-assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          projectId: projectId || undefined,
          estimateId: estimateId || undefined,
          messages: outgoing,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || 'Request failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete tail for next chunk
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') continue;
          try {
            applyEvent(JSON.parse(payload));
          } catch { /* skip malformed */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        streamingIdxRef.current = null;
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `⚠️ Sorry, something went wrong: ${err.message}. Please try again.` },
        ]);
      }
    } finally {
      streamingIdxRef.current = null;
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestions = [
    'Find the GA drawing and bookmark it for me',
    'List the folders in the root',
    'Share my bookmarks with the whole team',
  ];
  const showSuggestions = messages.filter((m) => m.role === 'user').length === 0;

  if (!projectId && !estimateId) return null;

  // ── Collapsed: slim vertical tab pinned to the right edge ──────────────────
  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => toggleOpen(true)}
        title="Open SharePoint Assistant"
        aria-label="Open SharePoint Assistant"
        style={styles.edgeTab}
      >
        <span style={{ fontSize: 15 }}>💬</span>
        <span style={styles.edgeTabText}>Assistant</span>
      </button>
    );
  }

  return (
    <div style={styles.panel}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ minWidth: 0 }}>
          <div style={styles.headerTitle}>
            <span aria-hidden="true">💬</span> SharePoint Assistant
          </div>
          <div style={styles.headerSub}>Searches this {scopeNoun} only</div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button type="button" onClick={clearChat} title="Clear chat" style={styles.headerBtn}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => toggleOpen(false)}
            title="Collapse"
            aria-label="Collapse assistant"
            style={styles.headerBtn}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div style={styles.messages}>
        {messages.map((msg, i) => {
          if (msg.role === 'tool') {
            return (
              <div key={i} style={styles.toolRow}>
                <span
                  style={{
                    ...styles.toolChip,
                    ...(msg.status === 'error' ? styles.toolChipError : {}),
                    ...(msg.status === 'running' ? styles.toolChipRunning : {}),
                  }}
                >
                  <span aria-hidden="true" style={{ marginRight: 5 }}>
                    {msg.status === 'running' ? '⏳' : msg.status === 'error' ? '⚠️' : TOOL_ICONS[msg.name] || '✓'}
                  </span>
                  {msg.content}
                </span>
              </div>
            );
          }
          const isUser = msg.role === 'user';
          return (
            <div key={i} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
              <div style={{ ...styles.bubble, ...(isUser ? styles.userBubble : styles.assistantBubble) }}>
                {isUser
                  ? msg.content
                  : <span dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />}
              </div>
            </div>
          );
        })}
        {isLoading && streamingIdxRef.current == null && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
            <div style={{ ...styles.bubble, ...styles.assistantBubble, color: '#94a3b8' }}>Thinking…</div>
          </div>
        )}
        {showSuggestions && !isLoading && (
          <div style={styles.suggestions}>
            {suggestions.map((s) => (
              <button key={s} type="button" style={styles.suggestionBtn} onClick={() => sendMessage(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={styles.inputArea}>
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='e.g. "find the GA drawing and bookmark it"'
          disabled={isLoading}
          style={styles.input}
        />
        <button
          type="button"
          onClick={() => sendMessage()}
          disabled={isLoading || !input.trim()}
          aria-label="Send"
          style={{ ...styles.sendBtn, opacity: isLoading || !input.trim() ? 0.5 : 1 }}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const styles = {
  edgeTab: {
    position: 'fixed',
    top: '45%',
    right: 0,
    zIndex: 8500,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    padding: '12px 7px',
    background: '#0f766e',
    color: '#fff',
    border: 'none',
    borderRadius: '10px 0 0 10px',
    cursor: 'pointer',
    boxShadow: '-2px 2px 10px rgba(15, 23, 42, 0.25)',
  },
  edgeTabText: {
    writingMode: 'vertical-rl',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.06em',
  },
  panel: {
    position: 'fixed',
    top: 'var(--header-height, 56px)',
    right: 0,
    bottom: 0,
    width: 'min(400px, 94vw)',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    borderLeft: '1px solid #e2e8f0',
    boxShadow: '-10px 0 28px rgba(15, 23, 42, 0.14)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    background: '#0f766e',
    color: '#fff',
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  headerSub: {
    fontSize: 11,
    opacity: 0.85,
    marginTop: 1,
  },
  headerBtn: {
    background: 'rgba(255,255,255,0.12)',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    padding: '6px 7px',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px 14px 8px',
    background: '#f8fafc',
  },
  bubble: {
    maxWidth: '88%',
    padding: '9px 12px',
    borderRadius: 12,
    fontSize: '0.85rem',
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  userBubble: {
    background: '#0f766e',
    color: '#fff',
    borderBottomRightRadius: 4,
    whiteSpace: 'pre-wrap',
  },
  assistantBubble: {
    background: '#fff',
    color: '#0f172a',
    border: '1px solid #e2e8f0',
    borderBottomLeftRadius: 4,
  },
  toolRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    margin: '2px 0 8px',
  },
  toolChip: {
    fontSize: 12,
    color: '#334155',
    background: '#eef2f7',
    border: '1px solid #e2e8f0',
    borderRadius: 999,
    padding: '3px 10px',
    maxWidth: '95%',
  },
  toolChipRunning: {
    color: '#0f766e',
    background: '#f0fdfa',
    border: '1px solid #99f6e4',
  },
  toolChipError: {
    color: '#b91c1c',
    background: '#fef2f2',
    border: '1px solid #fecaca',
  },
  suggestions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    margin: '4px 0 8px',
  },
  suggestionBtn: {
    textAlign: 'left',
    background: '#fff',
    border: '1px dashed #99f6e4',
    color: '#0f766e',
    borderRadius: 8,
    padding: '7px 10px',
    fontSize: 12.5,
    cursor: 'pointer',
  },
  inputArea: {
    display: 'flex',
    gap: 8,
    padding: '10px 12px',
    borderTop: '1px solid #e2e8f0',
    background: '#fff',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    padding: '9px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    fontSize: '0.85rem',
    outline: 'none',
    background: '#f8fafc',
  },
  sendBtn: {
    width: 38,
    height: 38,
    borderRadius: 8,
    background: '#0f766e',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
};

export default SharePointAssistant;
